import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap, map } from 'rxjs';
import { ApiService } from '../api.service';
import { Participant } from '../models';

@Injectable({ providedIn: 'root' })
export class ParticipantDomainService {
  private readonly api = inject(ApiService);

  private readonly participantsSignal = signal<Participant[] | null>(null);
  readonly participants = computed(() => this.participantsSignal());

  private readonly projectParticipantsSignal = signal<Participant[] | null>(null);
  readonly projectParticipants = computed(() => this.projectParticipantsSignal());

  loadParticipants(): Observable<Participant[]> {
    return this.api
      .getParticipants()
      .pipe(tap((participants) => this.participantsSignal.set(participants)));
  }

  loadProjectParticipants(projectId: string): Observable<Participant[]> {
    return this.api
      .getProjectParticipants(projectId)
      .pipe(tap((participants) => this.projectParticipantsSignal.set(participants)));
  }

  inviteParticipants(projectId: string, emails: string[]): Observable<Participant[]> {
    return this.api.inviteProjectParticipants(projectId, emails).pipe(
      tap((created) => {
        const current = this.projectParticipantsSignal();
        this.projectParticipantsSignal.set(current ? [...current, ...created] : created);
      }),
    );
  }

  updateParticipant(participant: Participant): Observable<void> {
    return this.api.updateParticipant(participant).pipe(
      tap(() => {
        const all = this.participantsSignal();
        if (all) {
          this.participantsSignal.set(all.map((p) => (p.id === participant.id ? participant : p)));
        }

        const project = this.projectParticipantsSignal();
        if (project) {
          this.projectParticipantsSignal.set(
            project.map((p) => (p.id === participant.id ? participant : p)),
          );
        }
      }),
      map(() => void 0),
    );
  }
}
